import { createTheme, getContrastRatio } from '@mui/material/styles';
import merge from 'lodash/merge';
import {
  defaultSettings,
  defaultThemeOptions,
  extendThemeWithMixins,
  getParsedQuerySettings,
  mustHaveThemeOptions,
} from '@fuse/default-settings';
import settingsConfig from 'app/configs/settingsConfig';
import themeLayoutConfigs from 'app/theme-layouts/themeLayoutConfigs';
import { darkPaletteText, lightPaletteText } from 'app/configs/themesConfig';
import { create } from 'zustand';

function getInitialSettings() {
  const defaultLayoutStyle =
    settingsConfig.layout && settingsConfig.layout.style ? settingsConfig.layout.style : 'layout1';
  const layout = {
    style: defaultLayoutStyle,
    config: themeLayoutConfigs[defaultLayoutStyle].defaults,
  };
  return merge({}, defaultSettings, { layout }, settingsConfig, getParsedQuerySettings());
}

export function generateSettings(_defaultSettings, _newSettings) {
  const response = merge(
    {},
    _defaultSettings,
    { layout: { config: themeLayoutConfigs[_newSettings?.layout?.style]?.defaults } },
    _newSettings,
  );

  return response;
}

export function generateMuiTheme(theme, direction) {
  const data = merge({}, defaultThemeOptions, theme, mustHaveThemeOptions);
  const response = createTheme(
    merge({}, data, {
      mixins: extendThemeWithMixins(data),
      direction,
    }),
  );
  return response;
}

function changeThemeMode(theme, mode) {
  const modes = {
    dark: {
      palette: {
        mode: 'dark',
        divider: 'rgba(241,245,249,.12)',
        background: {
          paper: '#1E2125',
          default: '#121212',
        },
        text: darkPaletteText,
      },
    },
    light: {
      palette: {
        mode: 'light',
        divider: '#e2e8f0',
        background: {
          paper: '#FFFFFF',
          default: '#F7F7F7',
        },
        text: lightPaletteText,
      },
    },
  };

  return merge({}, theme, modes[mode]);
}

const initialSettings = getInitialSettings();

export interface AppSettings {
  customScrollbars: true;
  direction: 'ltr';
  layout: {
    config: {
      navbar: {
        position: 'left' | 'right';
        display: boolean;
      };
    };
  };
  theme: {
    main: unknown;
    navbar: unknown;
    toolbar: unknown;
    footer: unknown;
  };
}

export interface SettingsSlice {
  initial: AppSettings;
  defaults: AppSettings;
  current: AppSettings;
  setSettings: (settings: AppSettings) => void;
}

export const useSettingsSlice = create<SettingsSlice>((set) => ({
  initial: initialSettings,
  defaults: merge({}, initialSettings),
  current: merge({}, initialSettings),
  setSettings: (payload) =>
    set((state) => {
      const current = generateSettings(state.defaults, payload);
      return {
        ...state,
        current,
      };
    }),
}));

export const getDirection = (state) => state.current.direction;
export const getMainTheme = (state) => state.current.theme.main;
export const getNavbarTheme = (state) => state.current.theme.navbar;
export const getToolbarTheme = (state) => state.current.theme.toolbar;

export const selectMainTheme = (state) => {
  return generateMuiTheme(getMainTheme(state), getDirection(state));
};
export const selectToolbarTheme = (state) => {
  return generateMuiTheme(getToolbarTheme(state), getDirection(state));
};
export const selectNavbarTheme = (state) => {
  return generateMuiTheme(getNavbarTheme(state), getDirection(state));
};

export const selectMainThemeDark = (state) => {
  generateMuiTheme(changeThemeMode(getMainTheme(state), 'dark'), getDirection(state));
};

export const selectMainThemeLight = (state) => {
  generateMuiTheme(changeThemeMode(getMainTheme(state), 'light'), getDirection(state));
};

export const selectContrastMainTheme = (bgColor) => {
  function isDark(color) {
    return getContrastRatio(color, '#ffffff') >= 3;
  }
  return isDark(bgColor) ? selectMainThemeDark : selectMainThemeLight;
};
